// Compute the next 11 AM in America/New_York as a Date object (UTC under the hood),
// then format it in the user's chosen timezone. Handles DST automatically.

const NY_TZ = "America/New_York";
const STREAM_HOUR = 11; // 11 AM EST

// Get the NY-local "now" parts.
const getNyParts = (date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: NY_TZ,
        year: "numeric", month: "numeric", day: "numeric",
        hour: "numeric", minute: "numeric", second: "numeric",
        hour12: false,
    }).formatToParts(date);
    const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
    return {
        year: get("year"),
        month: get("month") - 1,
        day: get("day"),
        hour: get("hour") === 24 ? 0 : get("hour"),
        minute: get("minute"),
    };
};

// Map "23:00 in NY on the given NY-calendar day" to a UTC Date via fix-point iteration.
const nyMomentToUtc = (year, month, day, hour, minute) => {
    let candidate = new Date(Date.UTC(year, month, day, hour, minute));
    for (let i = 0; i < 4; i++) {
        const cand = getNyParts(candidate);
        const candTotalMin = cand.hour * 60 + cand.minute + (cand.day !== day ? (cand.day > day ? 24 * 60 : -24 * 60) : 0);
        const targetTotalMin = hour * 60 + minute;
        const diff = targetTotalMin - candTotalMin;
        if (Math.abs(diff) < 1) break;
        candidate = new Date(candidate.getTime() + diff * 60000);
    }
    return candidate;
};

// Returns Date of the next upcoming 11 AM EST stream relative to now.
// If today's 11 AM EST has already passed, returns tomorrow's.
// If `skipToday` is true, today's slot is always skipped regardless of the clock
// (used when the owner has forced today's stream to OFFLINE).
export const getNextStreamDate = (now = new Date(), { skipToday = false } = {}) => {
    const ny = getNyParts(now);
    let targetDay = ny.day;
    if (ny.hour >= STREAM_HOUR || skipToday) targetDay = ny.day + 1;
    // Note: setting day = 32 etc. is handled correctly by Date.UTC
    return nyMomentToUtc(ny.year, ny.month, targetDay, STREAM_HOUR, 0);
};

// Format the next stream time in a target timezone.
// Returns: { dayLabel: "Tonight" | "Mon", timeLabel: "8:00 PM", offsetLabel: "in 3h 12m" }
export const getStreamInfoForTz = (timezone, now = new Date(), { skipToday = false } = {}) => {
    const nextStream = getNextStreamDate(now, { skipToday });
    const msUntil = nextStream - now;

    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "long",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    const parts = formatter.formatToParts(nextStream);
    const weekday = parts.find((p) => p.type === "weekday")?.value || "";
    const hour = parts.find((p) => p.type === "hour")?.value || "";
    const minute = parts.find((p) => p.type === "minute")?.value || "00";
    const ampm = parts.find((p) => p.type === "dayPeriod")?.value || "";

    // Determine day label
    const userToday = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "long",
        year: "numeric", month: "numeric", day: "numeric",
    }).format(now);
    const userTomorrow = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "long",
        year: "numeric", month: "numeric", day: "numeric",
    }).format(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    const streamDayStr = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "long",
        year: "numeric", month: "numeric", day: "numeric",
    }).format(nextStream);

    let dayLabel = weekday;
    if (streamDayStr === userToday) dayLabel = "Today";
    else if (streamDayStr === userTomorrow) dayLabel = "Tomorrow";

    const timeLabel = `${hour}:${minute} ${ampm}`;

    // Human-readable countdown
    const totalMin = Math.max(0, Math.floor(msUntil / 60000));
    const hours = Math.floor(totalMin / 60);
    const minutes = totalMin % 60;
    let countdown;
    if (totalMin <= 0) countdown = "starting";
    else if (hours === 0) countdown = `in ${minutes}m`;
    else if (hours < 24) countdown = `in ${hours}h ${minutes}m`;
    else countdown = `in ${Math.floor(hours / 24)}d ${hours % 24}h`;

    // Timezone short name
    const tzShortFmt = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "short",
    });
    const tzParts = tzShortFmt.formatToParts(nextStream);
    const tzShort = tzParts.find((p) => p.type === "timeZoneName")?.value || timezone;

    return {
        dayLabel,
        timeLabel,
        countdown,
        tzShort,
        weekdayFull: weekday,
        streamDate: nextStream,
    };
};

// Convert "11 AM EST" on a specific weekday into the user's local time string.
// dayIdx: 0=Sun, 1=Mon, ..., 6=Sat
export const convertScheduleSlotForTz = (timezone, dayIdx, hour = STREAM_HOUR, minute = 0, now = new Date()) => {
    // Find the next occurrence of this weekday in NY.
    const ny = getNyParts(now);
    const todayWeekday = new Date(Date.UTC(ny.year, ny.month, ny.day)).getUTCDay();
    let dayDelta = (dayIdx - todayWeekday + 7) % 7;
    if (dayDelta === 0 && ny.hour >= hour) dayDelta = 7;
    const targetUtc = nyMomentToUtc(ny.year, ny.month, ny.day + dayDelta, hour, minute);

    const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "short",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    const parts = fmt.formatToParts(targetUtc);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    return {
        time: `${get("hour")}:${get("minute")} ${get("dayPeriod")}`,
        weekday: get("weekday"),
        date: targetUtc,
    };
};
